import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';

const SubscriptionContext = createContext();

export function SubscriptionProvider({ children }) {
    const [subscription, setSubscription] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // fetchSubscription(); // Removed to avoid double-call (auth listener handles init)

        // Subscribe to auth changes
        const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
            // Only fetch if we have a user or if sign out happened
            if (session?.user || event === 'SIGNED_OUT') {
                fetchSubscription();
            }
        });

        return () => {
            authListener?.subscription?.unsubscribe();
        };
    }, []);

    const fetchSubscription = async () => {
        try {
            const { data: { user } } = await supabase.auth.getUser();

            if (!user) {
                setSubscription({ plan_type: 'free', status: 'active' });
                setLoading(false);
                return;
            }

            const { data, error } = await supabase
                .from('subscriptions')
                .select('*')
                .eq('user_id', user.id)
                .in('status', ['active', 'trialing'])
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle();

            if (error) {
                console.error('Error fetching subscription:', error);
            }

            setSubscription(data || { plan_type: 'free', status: 'active' });

            // Critical: Always verify with Polar to ensure accuracy/self-healing
            // This fixes cases where webhooks failed or DB is out of sync
            verifySubscription().then(verifiedSub => {
                if (verifiedSub && verifiedSub.plan_type) {
                    setSubscription(prev => {
                        // Only update if plan changed to avoid renders
                        if (prev?.plan_type !== verifiedSub.plan_type) {
                            console.log('Subscription corrected by Polar API verification:', verifiedSub.plan_type);
                            return verifiedSub;
                        }
                        return prev;
                    });
                }
            });

            setLoading(false);
        } catch (error) {
            console.error('Error in fetchSubscription:', error);
            setSubscription({ plan_type: 'free', status: 'active' });
            setLoading(false);
        }
    };

    // Verify subscription directly with Polar API (for critical actions like publishing)
    const verifySubscription = async () => {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                return { plan_type: 'free', status: 'active', verified: false };
            }

            console.log('Verifying subscription with Polar...');

            const response = await fetch(
                `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/verify-subscription`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`
                    },
                    body: JSON.stringify({
                        userId: user.id,
                        email: user.email
                    })
                }
            );

            if (!response.ok) {
                console.error('Verification failed, using cached data');
                return { ...subscription, verified: false };
            }

            const verifiedData = await response.json();
            console.log('Verified subscription:', verifiedData);

            // Update local state with verified data
            setSubscription(prev => ({
                ...prev,
                plan_type: verifiedData.plan_type,
                status: verifiedData.status,
                current_period_end: verifiedData.current_period_end,
                cancel_at_period_end: verifiedData.cancel_at_period_end,
            }));

            return verifiedData;
        } catch (error) {
            console.error('Error verifying subscription:', error);
            return { ...subscription, verified: false };
        }
    };

    // Use this before critical actions (like publishing) to ensure fresh data
    const verifyAndCanPublish = async () => {
        const verified = await verifySubscription();
        const planType = verified.plan_type || 'free';

        // Check if the verified plan can publish
        const canPublishPlans = ['starter', 'growth', 'scale'];
        return canPublishPlans.includes(planType);
    };

    const getPlanLimits = () => {
        const plan = subscription?.plan_type || 'free';
        // Add any purchased bonus views to the monthly limit
        const bonusViews = subscription?.bonus_views || 0;

        switch (plan) {
            case 'scale':
                return {
                    projects: Infinity,
                    screensPerProject: Infinity,
                    publishedFlows: Infinity,
                    monthlyViews: 500000 + bonusViews,
                    variants: Infinity,
                    analyticsRetentionDays: Infinity,
                    abTesting: true,
                    canPublish: true,
                    showBranding: false,
                    prioritySupport: true,
                    // AI limits removed but kept for future reactivation
                    // aiGenerations: Infinity,
                    // aiModifications: Infinity
                };
            case 'growth':
                return {
                    projects: Infinity,
                    screensPerProject: Infinity,
                    publishedFlows: Infinity,
                    monthlyViews: 100000 + bonusViews,
                    variants: Infinity,
                    analyticsRetentionDays: Infinity,
                    abTesting: true,
                    canPublish: true,
                    showBranding: false,
                    prioritySupport: true,
                    // aiGenerations: 100,
                    // aiModifications: 500
                };
            case 'starter':
                return {
                    projects: Infinity,
                    screensPerProject: Infinity,
                    publishedFlows: Infinity,
                    monthlyViews: 25000 + bonusViews,
                    variants: 3,
                    analyticsRetentionDays: Infinity,
                    abTesting: true,
                    canPublish: true,
                    showBranding: true,
                    prioritySupport: false,
                    // aiGenerations: 25,
                    // aiModifications: 100
                };
            case 'free':
            default:
                return {
                    projects: 1,
                    screensPerProject: Infinity,
                    publishedFlows: 0, // Cannot publish - draft/test mode only
                    monthlyViews: 1000 + bonusViews,
                    variants: 0,
                    analyticsRetentionDays: 7,
                    abTesting: false,
                    canPublish: false,
                    showBranding: true,
                    prioritySupport: false,
                    // aiGenerations: 3,
                    // aiModifications: 10
                };
        }
    };

    const canCreateProject = async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return false;

        // Verify subscription with Polar API to get accurate plan type
        const verified = await verifySubscription();
        const planType = verified?.plan_type || subscription?.plan_type || 'free';

        // Get limits based on verified plan
        const limits = {
            'scale': Infinity,
            'growth': Infinity,
            'starter': Infinity,
            'free': 1
        };
        const projectLimit = limits[planType] || 1;

        // Count existing projects
        const { count } = await supabase
            .from('projects')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', user.id);

        console.log(`canCreateProject: plan=${planType}, limit=${projectLimit}, current=${count}`);
        return count < projectLimit;
    };

    const canAddScreen = (currentScreenCount) => {
        // Allow building unlimited screens, check limits only on publish
        return true;
    };

    const checkProjectLimits = (project) => {
        const limits = getPlanLimits();
        const screenCount = project.screens?.length || 0;

        if (limits.screensPerProject !== Infinity && screenCount > limits.screensPerProject) {
            return {
                allowed: false,
                reason: 'screens',
                limit: limits.screensPerProject,
                current: screenCount
            };
        }
        return { allowed: true };
    };

    const canPublish = async (projectId) => {
        const limits = getPlanLimits();
        return limits.canPublish;
    };

    const canUseABTesting = () => {
        const limits = getPlanLimits();
        return limits.abTesting;
    };

    const canCreateVariant = (currentVariantCount) => {
        const limits = getPlanLimits();
        return currentVariantCount < limits.variants;
    };

    const getCurrentMonthViews = async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return 0;

        const startOfMonth = new Date();
        startOfMonth.setDate(1);
        startOfMonth.setHours(0, 0, 0, 0);

        // Get all user's projects
        const { data: projects } = await supabase
            .from('projects')
            .select('id')
            .eq('user_id', user.id);

        if (!projects || projects.length === 0) return 0;

        const projectIds = projects.map(p => p.id);

        // Count unique sessions (users) from analytics_events
        // We use a raw query to count distinct session_ids
        const { data, error } = await supabase
            .rpc('count_unique_users', {
                project_ids: projectIds,
                start_date: startOfMonth.toISOString()
            });

        if (error) {
            console.error('Error counting unique users:', error);
            // Fallback to old method if RPC fails
            const { count } = await supabase
                .from('flow_views')
                .select('*', { count: 'exact', head: true })
                .eq('user_id', user.id)
                .gte('viewed_at', startOfMonth.toISOString());
            return count || 0;
        }

        return data || 0;
    };

    const checkViewLimitState = async () => {
        const currentViews = await getCurrentMonthViews();
        const limits = getPlanLimits();
        const isOverLimit = currentViews >= limits.monthlyViews;

        return {
            isOverLimit,
            currentViews,
            limit: limits.monthlyViews
        };
    };

    // Soft limit check for public views (always returns false to allow "grace period" viewing)
    // In the future, this can check a "first_exceeded_date" to block after 10 days.
    const hasReachedViewLimit = async () => {
        // We fundamentally allow views to continue (soft limit)
        // strict blocking happens only if we implement the 10-day hard stop later.
        return false;
    };

    const canEditProject = async () => {
        const { isOverLimit } = await checkViewLimitState();
        // If over view limit, block editing (strict enforcement)
        if (isOverLimit) {
            return false;
        }
        return true;
    };

    const canUseAIGeneration = async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return { allowed: false, reason: 'Not authenticated' };

        // Check monthly limit
        const { data: monthlyCount, error: monthlyError } = await supabase
            .rpc('count_monthly_ai_usage', {
                p_user_id: user.id,
                p_usage_type: 'generation'
            });

        if (monthlyError) {
            console.error('Error checking monthly AI usage:', monthlyError);
            return { allowed: false, reason: 'Error checking usage' };
        }

        const limits = getPlanLimits();
        if (monthlyCount >= limits.aiGenerations) {
            return {
                allowed: false,
                reason: 'monthly_limit',
                current: monthlyCount,
                limit: limits.aiGenerations
            };
        }

        // Check hourly rate limit (10 per hour)
        const { data: hourlyCount, error: hourlyError } = await supabase
            .rpc('count_hourly_ai_usage', {
                p_user_id: user.id,
                p_usage_type: 'generation'
            });

        if (hourlyError) {
            console.error('Error checking hourly AI usage:', hourlyError);
        } else if (hourlyCount >= 10) {
            return {
                allowed: false,
                reason: 'rate_limit',
                message: 'Too many requests. Please wait an hour.'
            };
        }

        return { allowed: true, current: monthlyCount, limit: limits.aiGenerations };
    };

    const trackAIUsage = async (usageType, projectId = null) => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        await supabase.from('ai_usage').insert({
            user_id: user.id,
            usage_type: usageType,
            project_id: projectId
        });
    };

    const getMonthlyAIUsage = async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return { generations: 0, modifications: 0 };

        const { data: genCount } = await supabase
            .rpc('count_monthly_ai_usage', {
                p_user_id: user.id,
                p_usage_type: 'generation'
            });

        const { data: modCount } = await supabase
            .rpc('count_monthly_ai_usage', {
                p_user_id: user.id,
                p_usage_type: 'modification'
            });

        return {
            generations: genCount || 0,
            modifications: modCount || 0
        };
    };

    return (
        <SubscriptionContext.Provider
            value={{
                subscription,
                loading,
                canCreateProject,
                canAddScreen,
                canPublish,
                checkProjectLimits,
                getPlanLimits,
                canUseABTesting,
                canCreateVariant,
                getCurrentMonthViews,
                hasReachedViewLimit,
                checkViewLimitState,
                canEditProject,
                canUseAIGeneration,
                trackAIUsage,
                getMonthlyAIUsage,
                refresh: fetchSubscription,
                verifySubscription,
                verifyAndCanPublish
            }}
        >
            {children}
        </SubscriptionContext.Provider>
    );
}

export const useSubscription = () => {
    const context = useContext(SubscriptionContext);
    if (!context) {
        throw new Error('useSubscription must be used within SubscriptionProvider');
    }
    return context;
};
